import { useTranslation } from 'react-i18next'
import { Button } from '@structural-climbing/ui'

interface Props {
  onLogTop: () => void
  /** Opens this wall's standings, which the last step points to. */
  onOpenStandings: () => void
}

/**
 * In place of the session stats for someone with no tops yet on this wall.
 *
 * "0 tops", an empty rank and a row of everything still to climb all describe
 * what they have not done. This says how a top is logged and asks for the
 * first one. It goes for good once the board has a row for them.
 */
export function FirstTopCard({ onLogTop, onOpenStandings }: Props) {
  const { t } = useTranslation()
  const button = t('session.logTop')
  const steps = [
    t('session.firstTopStep1'),
    t('session.firstTopStep2'),
    t('session.firstTopStep3', { button }),
    // Three keys rather than <Trans>, so each language orders its own words
    // around the link and the link always has its text.
    <>
      {t('session.firstTopStep4Before')}{' '}
      <Button
        variant="light"
        disableRipple
        onPress={onOpenStandings}
        className="inline h-auto min-w-0 rounded-none bg-transparent p-0 align-baseline text-[15px] font-semibold text-[var(--gym-primary)] underline underline-offset-2 data-[hover=true]:bg-transparent"
      >
        {t('session.firstTopStep4Link')}
      </Button>
      {t('session.firstTopStep4After')}
    </>,
  ]

  return (
    <section
      data-testid="first-top"
      aria-label={t('session.firstTopEyebrow')}
      className="flex flex-col gap-4 rounded-lg border p-4 shadow-e1"
      style={{
        borderColor: 'color-mix(in srgb, var(--gym-primary) 30%, transparent)',
        background:
          'linear-gradient(170deg, color-mix(in srgb, var(--gym-primary) 10%, white) 0%, white 60%)',
      }}
    >
      <p className="eyebrow text-[var(--gym-primary)]">{t('session.firstTopEyebrow')}</p>
      <ol className="flex flex-col gap-3">
        {steps.map((step, i) => (
          <li key={i} className="flex items-start gap-3 text-[15px] leading-snug">
            <span
              aria-hidden
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[color-mix(in_srgb,var(--gym-primary)_18%,white)] text-xs font-extrabold text-foreground/70"
            >
              {i + 1}
            </span>
            <span className="pt-0.5">{step}</span>
          </li>
        ))}
      </ol>
      <Button
        size="lg"
        className="w-full bg-[var(--gym-primary)] font-bold text-[var(--gym-on-primary)]"
        onPress={onLogTop}
        startContent={
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.4}
            strokeLinecap="round"
            className="h-5 w-5"
            aria-hidden
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
        }
      >
        {button}
      </Button>
    </section>
  )
}
